// Per-file diff state for the explorer: texts (from blobs by SHA), engine
// blocks, highlighting and moves. The visible file loads first; the rest are
// prefetched in the background so progress, search and moves cover the PR.
import { blob, blobCached, contentAt, graphql, rest } from '../api/gh';
import type { Priority } from '../api/scheduler';
import { HttpError, LruMap } from '../api/cache';
import { failureOf, type Failure } from '../api/failure';
import { blobShasQuery, type PrFile } from '../api/queries';
import { eng, engineDegraded, prioritizeHighlight, urgentHighlight } from '../diff/engine';
import { fileBlocks, type Shortfall } from '../diff/fileBlocks';
import { identityGeneration } from '../api/identity';
import type { Anchor, Block, BlockSet, DiffAlgorithm, FileSides, Highlight, Move, Scope, Span } from '../engine/types';
import { categorize, type Category } from '../logic/categorize';
import { applyPatch } from '../logic/patch';
import { blockCoverage, blockReviewed, type Coverage } from '../logic/partial';
import { blockScores, movedRows } from '../logic/complexity';
import { agentBlocks, provideComplexity, type AgentFileComplexity } from './complexityRegistry';
import type { DiffSource } from './rangeHost';
import { compareHead, inPath, pathOf, subjectKey, type Subject } from '../subject';
import { EMPTY_TREE } from '../logic/track';
import { trackPoints } from './fileNotes.svelte';
import { blobsOnTrackPerPath, type FileOnTrack } from './trackBlobs';
import { RepoTree } from './repotree.svelte';
import { DEFAULT_ALGORITHM } from '../diff/algorithms';
import { textHash } from '../logic/textHash';
import { baseHeadIdentity, fileIdentity, idbStatusStore, marksSignature, statusKeyOf, type FileStatus, type StatusRecord, type StatusStore } from './reviewStatus';
import { anchorId, blobId, type Line, type Plan } from '../review/anchors';
import {
  carryNeeds,
  changesOf,
  lineMapKey,
  planFile,
  planLines,
  ReviewCache,
  statusOf,
  type FileReview,
  type ReviewDiff,
  type ReviewFile,
  type ReviewModel,
} from '../review/model';
import { idbLineMapStore, type LineMapStore } from '../review/lineMaps';
import type { MarksResolution } from '../api/server';
import { toWire } from '../review/anchors';

/** Largest file (bytes, or UTF-16 units once loaded) shown without asking when opened from the repository tree. */
export const MAX_FILE_SIZE = 1024 * 1024;

export interface DiffMode {
  from: string | null;
  to: string | null;
  ignoreWhitespace: boolean;
  /** The alignment shown. */
  algorithm: DiffAlgorithm;
}

export interface FileEntry {
  path: string;
  previous: string | null;
  status: PrFile['status'];
  additions: number;
  deletions: number;
  /** GitHub's base…head patch (null for interdiffs not ending at head, or when GitHub omits it). */
  patch: string | null;
  /** Blob SHAs on each side, when known. */
  oldSha: string | null;
  newSha: string | null;
  category: Category;
  /** Changed between snapshots only because of a rebase (not a PR file). */
  noise: boolean;
  /** Removed and added lines, as GitHub counts them (at least 1 when the file changes): progress until the diff is computed. */
  estimate: number;
}

export interface FileDiff {
  /** `too-large`: a file the PR does not change, over MAX_FILE_SIZE, until `showLarge`. */
  status: 'loading' | 'ready' | 'error' | 'binary' | 'too-large';
  set: BlockSet | null;
  oldLines: string[];
  newLines: string[];
  oldHl: Span[][] | null;
  newHl: Span[][] | null;
  /** Each side's scopes (see `Scope`), from its highlight; null until highlighted. */
  oldScopes: Scope[] | null;
  newScopes: Scope[] | null;
  language: string | null;
  /** Why the file couldn't load (status `error`); null otherwise. */
  failure: Failure | null;
  /** What the shown diff lacks because the engine failed computing it in
   *  full (status `ready`, see `fileBlocks`); null or absent otherwise. */
  shortfall?: Shortfall | null;
  /** Each side's path and blob ID, which review marks name (status `ready`); null otherwise. */
  sides?: FileSides | null;
}

/**
 * For the session: per comparison and file text, the signature of the
 * pending key-based marks the file was looked through for and held none
 * of, so another model of the same comparison does not look again.
 */
const deadKeys = new LruMap<string, string>(5000);

/** A changed file's texts, and GitHub's blob SHA of each read from a blob. */
interface Texts {
  old: string;
  new: string;
  oldSha: string | null;
  newSha: string | null;
}

/**
 * Whether the engine scored `set`'s complexity: a plain diff and the
 * fallback's (a shortfall, or the engine unavailable) carry none, and
 * their files count as unknown, never as needing no thought.
 */
function scored(set: BlockSet): boolean {
  return set.complexity !== undefined;
}

function splitLines(text: string): string[] {
  if (text === '') return [];
  return (text.endsWith('\n') ? text.slice(0, -1) : text).split('\n');
}

/** Highlights by language and text content (old sides are often rebuilt text with no blob SHA). */
const hlCache = new LruMap<string, Promise<Highlight | null>>(400);

/** Cache key for a highlight: the content, never a path or ref whose text can change. */
export function highlightKey(language: string, text: string): string {
  return language + ':' + textHash(text);
}

/** `urgent` for the file the reader has open: it goes ahead of prefetch. */
function highlightCached(language: string, text: string, urgent = false): Promise<Highlight | null> {
  const k = highlightKey(language, text);
  let p = hlCache.get(k);
  if (p && urgent) prioritizeHighlight(language, text);
  if (!p) {
    p = (urgent ? urgentHighlight : eng.highlight)(language, text).catch(() => null);
    hlCache.set(k, p);
  }
  return p;
}

/**
 * The share of the user's request budget background prefetch may use, in
 * requests a second. The server allows 20 a second with bursts of 60
 * (docs/design.md, Quotas and rate limits); prefetch keeps well under it
 * so that what the viewer does next always has budget left.
 */
const PREFETCH_REQUESTS_PER_SECOND = 6;
/**
 * The most requests one file load makes: two blobs for an interdiff; the
 * head blob, the merge base and the base text otherwise.
 */
const REQUESTS_PER_FILE = 3;
/** How many recently opened files keep their highlights ahead of prefetch. */
export const OPENED_FILES = 4;
/**
 * The budget one request takes from prefetch: after starting a file, the
 * next waits this long for each request the file makes (`requestsFor`):
 * none for a file whose texts are in this browser already, one for a
 * file with a patch, up to `REQUESTS_PER_FILE`.
 */
export const REQUEST_GAP_MS = Math.ceil(1000 / PREFETCH_REQUESTS_PER_SECOND);
/** The longest wait after a prefetched file starts (about 500 ms): one making `REQUESTS_PER_FILE` requests. */
export const PREFETCH_GAP_MS = REQUESTS_PER_FILE * REQUEST_GAP_MS;
/** Prefetched files loading at once. */
export const PREFETCH_FILES = 2;
/**
 * How long a prefetched file holds its slot (30 s, the engine's budget
 * for a diff): past it the next queued file starts, while the slow one
 * carries on and shows when it finishes.
 */
export const PREFETCH_TIMEOUT_MS = 30_000;
/** How soon after the review coverage changes its statuses are kept for the next load (see `saveStatus`). */
const STATUS_SAVE_MS = 50;
/** Files the viewer asked for loading at once (they are not paced). */
const WANTED_FILES = 4;
/** How long after a line map failed for a passing reason it is tried again, doubling up to the maximum. */
export const CARRY_RETRY_MS = 2000;
export const CARRY_RETRY_MAX_MS = 60_000;
/** Readings of a file's blobs on the track before giving up until the viewer asks again (`retryHistory`). */
export const TRACK_TRIES = 5;
/** Line maps computed at once (each may fetch an earlier blob first). */
const CARRY_AT_ONCE = 2;

/** A subject's key as `subjectKey` wrote it before keys were canonical: its names as given. */
function writtenKey(s: Subject): string {
  const repo = `${s.owner}/${s.repo}`;
  if (s.kind === 'pull') return `${repo}#${s.number}`;
  if (s.kind === 'compare') return s.base === null ? `${repo}@${compareHead(s)}` : `${repo}@${s.base}...${compareHead(s)}`;
  return `${repo}:${s.ref}${s.path === null ? '' : '//' + s.path}`;
}

/** The blobs of each of `paths` at `points`, by every name each had there (`state/trackBlobs.ts` `blobsOnTrackPerPath`). */
export type TrackBlobs = (owner: string, repo: string, points: readonly string[], paths: readonly string[], at: string | null) => Promise<Map<string, FileOnTrack>>;

export class DiffModel {
  entries: FileEntry[] = $state.raw([]);
  status: 'loading' | 'ready' | 'error' = $state('loading');
  /** Why the changed files couldn't load (status `error`); null otherwise. */
  failure: Failure | null = $state(null);
  /**
   * base…head opens on the changed files' listing as it goes (`init`):
   * while it does, how many it holds of how many the pull request says it
   * changes, the entries so far being provisional (not yet confirmed
   * against the head). Null once every file is listed, and for an
   * interdiff, which waits for every file.
   */
  listing: { listed: number; total: number | null } | null = $state(null);
  /** Why the listing stopped before every file was listed (the files so far stay shown); null otherwise. */
  listingFailure: Failure | null = $state(null);
  /**
   * Files whose blobs on the track could not all be read in TRACK_TRIES
   * attempts: marks on their earlier versions may be missing, so they stay
   * not known (`reviewKnown`) until `retryHistory` reads them.
   */
  historyFailed: ReadonlySet<string> = $state.raw(new Set());
  diffs: Record<string, FileDiff> = $state.raw({});
  moves: Move[] = $state.raw([]);
  /** Short description of the comparison shown in the header. */
  label: string = $state('');
  /** Head this model was built for; differs from the track's head once new commits are pushed. */
  head: string | null = $state(null);
  /** The interdiff covers only the PR's files (a recursive tree was truncated). */
  partial = $state(false);
  /** Commit whose text the new side shows (the head for base…head), once known. */
  newCommit: string | null = $state(null);
  /** Commit whose text the old side shows (the merge base for base…head), once resolved (`resolveOldCommit`). */
  oldCommit: string | null = $state(null);
  /** Every file of the repository at the head (explorer "All files"); created by `init`, loaded on demand. */
  repoTree: RepoTree | null = $state(null);
  /** The reviewer's reviewed marks (stored and pending), as `setMarks` was last given them. */
  marks: readonly Anchor[] = $state.raw([]);
  /** Line maps computed so far, by `lineMapKey(from, to)` (see `carry`). */
  lineMaps: ReadonlyMap<string, readonly number[]> = $state.raw(new Map());
  /** Each side's text by blob ID, for files diffed: the later blob of a line map. */
  private blobTexts = new Map<string, string>();
  /**
   * The (path, blob) pairs this comparison asks marks of, as `path\0blob`:
   * each diffed file's sides (and a side's computed ID where GitHub's oid
   * names its text, see `aliases`), and the file's blobs at the track's
   * points by every name it had there. The review model reads marks on
   * these alone: the marks store holds marks on other versions of a path
   * too, which must never be carried in.
   */
  private asked: ReadonlySet<string> = $state.raw(new Set());
  /** Each diffed file's other names on the track (renames before the range shown), whose marks apply to it. */
  private trackNames: ReadonlyMap<string, readonly string[]> = $state.raw(new Map());
  /**
   * A side's computed ID (`blobId` of its decoded text) by its oid, where
   * they differ (a byte-order mark, invalid UTF-8): marks made before sides
   * were named by their oid are on it, and are read as the oid's.
   */
  private aliases = new Map<string, string>();
  /** Files whose marks on the track are not all known yet: their blobs there not named, or the marks store not answered. */
  private onTheWay: ReadonlySet<string> = $state.raw(new Set());
  /** Files whose blobs on the track are still to be asked for, and the asking under way. */
  private trackQueue: string[] = [];
  private trackAsking: Promise<void> | null = null;
  /** Where the track's history starts (its merge base), once known; the base until then. */
  private trackStart: string | null = $state(null);
  private startAsked = false;
  /** The points a file's blobs are asked at: the track's, recomputed as it grows (replaced commits found later). */
  private trackPointsNow: string[] = $derived.by(() => trackPoints(this.source.track, this.trackStart));
  /** For each file whose blobs on the track were all read, the points (joined) they were read at. */
  private trackReadAt = new Map<string, string>();
  /** Each file's failed readings in a row, and the timers asking again. */
  private trackFailures = new Map<string, number>();
  private trackRetries = new Set<ReturnType<typeof setTimeout>>();
  /** The (path, blob) pairs each file's marks are read from (`path\0blob`): its blobs listed, its sides, its blobs on the track. */
  private filePairs = new Map<string, Set<string>>();
  /** Line maps asked for (computing, done or given up on), by key. */
  private carried = new Set<string>();
  private carrying = 0;
  /** Failed attempts at each line map, by key, and the timers of their retries. */
  private carryFailures = new Map<string, number>();
  private carryRetries = new Set<ReturnType<typeof setTimeout>>();
  private carryTimer: ReturnType<typeof setTimeout> | null = null;
  /** Line maps asked for besides the review model's (`wantLineMaps`), by key. */
  private wantedMaps = new Map<string, { from: string; to: string }>();
  private reviewCache = new ReviewCache();
  /** A changed file as the review model reads it, per path, for the block set and sides it was built from. */
  private reviewFiles = new Map<string, { diff: FileDiff; file: ReviewFile; names: readonly string[] | undefined }>();
  /** A changed file not diffed yet as the review model reads it, per entry, for the kept status it was built with. */
  private pendingFiles = new WeakMap<FileEntry, { kept: FileStatus | undefined; file: ReviewFile }>();
  private coverages = new WeakMap<FileReview, Coverage[]>();
  private blockFlags = new WeakMap<FileReview, boolean[]>();
  /** The review statuses a previous load kept for this comparison (see reviewStatus.ts); null until read. */
  private kept: StatusRecord | null = $state.raw(null);
  private statusTimer: ReturnType<typeof setTimeout> | null = null;
  /** Per changed file, the requests loading it makes (see `REQUEST_GAP_MS`). */
  private requests = new Map<string, Promise<number>>();
  private pumping = false;
  /** Prefetched files' highlighting, held until every queued file is diffed (see `flushHighlights`). */
  private heldHighlights = new Map<string, () => Promise<void>>();
  /** The account the model was built for (`identityGeneration`): nothing is kept after it changes. */
  private generation = identityGeneration();
  /** Files the PR does not change, opened from the repository tree. */
  private unchanged = new Set<string>();

  /** Files the viewer asked for: loaded at once (see `ensure`). */
  private wanted: string[] = [];
  /** Files queued by `prefetch`: loaded in the background, paced. */
  private background: string[] = [];
  private activeWanted = 0;
  private activeBackground = 0;
  /** When the next prefetched file that makes requests may start (epoch ms). */
  private nextBackgroundAt = -Infinity;
  private paceTimer: ReturnType<typeof setTimeout> | null = null;
  private started = new Set<string>();
  /** The files the reader asked for (`ensure`) most recently, newest
   *  last, at most `OPENED_FILES`: their highlights go first. */
  private opened: string[] = [];
  /** The texts each loading file is highlighting, to promote them when the
   *  reader opens a file prefetch started. */
  private highlighting = new Map<string, [string, string][]>();
  private movesTimer: ReturnType<typeof setTimeout> | null = null;
  private mergeBases = new Map<string, Promise<string>>();
  private trees = new Map<string, Promise<Map<string, string> | null>>();
  private disposed = false;
  private shown = false;

  constructor(
    public source: DiffSource,
    public mode: DiffMode,
    private statusStore: StatusStore = idbStatusStore,
    private lineMapStore: LineMapStore = idbLineMapStore,
    private trackBlobs: TrackBlobs = (owner, repo, points, paths, at) => blobsOnTrackPerPath(owner, repo, points, paths, { at }),
  ) {}

  private get owner(): string {
    return this.source.subject.owner;
  }
  private get repo(): string {
    return this.source.subject.repo;
  }
  /** The track's head now (null while the subject is still loading). */
  private get trackHead(): string | null {
    return this.source.track.head || null;
  }

  /**
   * The comparison is an interdiff rather than base…head: decided once, in
   * `init`, against the head the model was built for, so a push never
   * changes it under the viewer (a new model is built for the new head).
   */
  interdiff = $state(false);
  /** The PR's base…head totals when the model was built. */
  private headTotals = $state({ added: 0, removed: 0 });

  async init() {
    try {
      // Files for the current head: joins a reload that a push started.
      const listed = this.source.loadFiles();
      // base…head opens on the listing's first page and grows as it goes; an interdiff waits for every file.
      if (!this.mode.from && !this.mode.to) {
        await this.source.filesGrew(0);
        const sofar = this.source.filesSoFar;
        if (sofar?.files.length && !this.disposed) return await this.openListing(sofar, listed);
      }
      await listed;
      const files = this.source.files;
      if (!files) {
        this.failure = this.source.filesFailure ?? failureOf(new Error('the files were not loaded'));
        this.status = 'error';
        return;
      }
      this.head = this.source.filesHead ?? this.trackHead;
      this.interdiff = !!(this.mode.from || (this.mode.to && this.mode.to !== this.head));
      if (this.shown && !this.disposed) this.source.showDiff(this, this.head);
      this.headTotals = { ...this.source.totals };
      if (this.head) this.repoTree = new RepoTree(this.owner, this.repo, this.head);
      if (this.interdiff) await this.initInterdiff(files);
      else {
        this.entries = files.map((f) => this.entryFor(f));
        this.askListed();
        this.label = 'base → head';
        this.newCommit = this.head;
        // Agents asking about this subject read this view's figures (relayTools), whatever its kind.
        const subject = this.source.subject;
        if (!this.disposed) this.unprovide = provideComplexity(subject, (path, sha) => this.agentComplexity(path, sha));
      }
      this.statusRead = this.readStatus();
      this.status = 'ready';
    } catch (e) {
      this.failure = failureOf(e);
      this.status = 'error';
    }
  }

  /**
   * What every file's blocks depend on besides its own texts: the pull
   * request, which comparison (base…head, or the commits an interdiff
   * names), the diff options and the engine. Not the base and head of
   * base…head: a file's entry is keyed by its blobs and patch
   * (`fileIdentity`), so after a push a file the push did not touch keeps
   * its status, and one it did gets none, rather than every file reading
   * as unreviewed until it is diffed again.
   */
  private statusKey(asWritten = false): string {
    return statusKeyOf(asWritten ? writtenKey(this.source.subject) : subjectKey(this.source.subject), this.mode);
  }

  /** Settles once the review statuses kept by a previous load are read (they show as soon as they are). */
  statusRead: Promise<void> = Promise.resolve();

  private async readStatus() {
    try {
      this.kept = (await this.statusStore.read(this.statusKey())) ?? null;
      // Kept before subject keys were canonical, under the subject's names as written: read there once (the next save keys it anew).
      const old = this.statusKey(true);
      if (!this.kept && old !== this.statusKey()) this.kept = (await this.statusStore.read(old)) ?? null;
    } catch {
      /* no kept statuses: files show their estimate until diffed */
    }
  }

  /**
   * Open base…head on the files a listing under way holds so far, then
   * take in each page as it lands, and the confirmed list at its end (see
   * `listing`).
   */
  private async openListing(sofar: { files: readonly PrFile[]; head: string | null; total: number | null }, listed: Promise<void>) {
    this.head = sofar.head ?? this.trackHead;
    if (this.shown && !this.disposed) this.source.showDiff(this, this.head);
    this.headTotals = { ...this.source.totals };
    if (this.head) this.repoTree = new RepoTree(this.owner, this.repo, this.head);
    this.label = 'base → head';
    this.newCommit = this.head;
    this.listing = { listed: sofar.files.length, total: sofar.total };
    this.takeListed(sofar.files);
    this.statusRead = this.readStatus();
    this.status = 'ready';
    for (let n = sofar.files.length; !this.disposed; ) {
      await this.source.filesGrew(n);
      const more = this.source.filesSoFar;
      if (!more) break;
      this.takeListed(more.files);
      this.listing = { listed: more.files.length, total: more.total };
      n = more.files.length;
    }
    await listed;
    if (this.disposed) return;
    const files = this.source.files;
    if (!files) {
      // The files so far stay, said to be partial; the listing's Try again lists them all.
      this.listingFailure = this.source.filesFailure ?? failureOf(new Error('the files were not loaded'));
      return;
    }
    this.takeListed(files);
    this.listing = null;
    this.listingFailure = null;
  }

  /**
   * The entries for `files` in their order, keeping each entry already
   * made for a path (its identity, and what was read for it, stay); a
   * file listed before but not now is dropped (a listing that started
   * again after a push).
   */
  private takeListed(files: readonly PrFile[]) {
    const had = new Map(this.entries.map((e) => [e.path, e]));
    this.entries = files.map((f) => {
      const e = had.get(f.filename);
      const same = e && e.status === f.status && e.newSha === (f.status === 'removed' ? null : f.sha) && e.patch === (f.patch ?? null);
      return same ? e : this.entryFor(f);
    });
    this.askListed();
  }

  /** List the changed files again after the listing stopped part way (the files so far stay shown meanwhile). */
  async retryListing() {
    if (!this.listingFailure) return;
    this.listingFailure = null;
    await this.source.loadFiles();
    const files = this.source.files;
    if (!files) {
      this.listingFailure = this.source.filesFailure ?? failureOf(new Error('the files were not loaded'));
      return;
    }
    this.takeListed(files);
    this.listing = null;
  }

  /** The entry of each changed file, by path. */
  private byPath = $derived(new Map(this.entries.map((e) => [e.path, e])));

  /** `fileIdentity` per entry (it hashes the patch). */
  private identities = new WeakMap<FileEntry, string>();
  /** For the marks now, the anchor IDs on each path. */
  private marksByPath: { marks: readonly Anchor[]; ids: Map<string, string[]>; sigs: Map<FileEntry, string> } | null = null;

  /** `marksSignature` of the marks that can apply to a changed file (on its paths, any blob). */
  private fileMarks(e: FileEntry): string {
    // The reviewer's marks on its paths as the store holds them, with what
    // pending key-based marks resolved here: a kept status holds for the
    // same, before the file is diffed and its pairs asked.
    const marks = this.storeMarks;
    if (this.marksByPath?.marks !== marks) {
      const ids = new Map<string, string[]>();
      for (const m of marks) {
        const list = ids.get(m.path);
        if (list) list.push(anchorId(m));
        else ids.set(m.path, [anchorId(m)]);
      }
      this.marksByPath = { marks, ids, sigs: new Map() };
    }
    const { ids, sigs } = this.marksByPath;
    let sig = sigs.get(e);
    if (sig === undefined) {
      const paths = new Set([e.path, ...(e.previous ? [e.previous] : [])]);
      sigs.set(e, (sig = marksSignature([...paths].flatMap((p) => ids.get(p) ?? []))));
    }
    return sig;
  }

  private identityOf(e: FileEntry): string {
    let id = this.identities.get(e);
    // Base…head names no old blob (`entryFor`): the identity the Branches tab looks it up by.
    if (id === undefined) this.identities.set(e, (id = e.oldSha === null ? baseHeadIdentity({ ...e, newSha: e.newSha }) : fileIdentity(e)));
    return id;
  }

  /** The status a previous load kept for `e`, when it was computed for the marks on its paths now. */
  private keptStatus(e: FileEntry): FileStatus | undefined {
    const k = this.kept;
    // The fallback lays changes out differently: a kept block flag would name another change.
    if (!k || engineDegraded()) return undefined;
    const f = k.files[this.identityOf(e)];
    return f && f.marks === this.fileMarks(e) ? f : undefined;
  }

  /**
   * Keep every changed file's review status that is computed (and waits
   * for no line map), for the next load of this comparison, each with the
   * marks on its paths it holds for; the statuses kept before for other
   * files stay. Nothing is kept while the engine is unavailable (the
   * fallback lays changes out differently), nor for a file shown plainer
   * (`shortfall`, the same), nor once the signed-in account changed.
   */
  async saveStatus(): Promise<void> {
    if (this.statusTimer) clearTimeout(this.statusTimer);
    this.statusTimer = null;
    if (this.status !== 'ready' || engineDegraded() || identityGeneration() !== this.generation) return;
    // The statuses kept before, of the files changed now: one record per
    // comparison outlives pushes, so a file no longer changed (or changed
    // anew, under another identity) is dropped rather than kept for good.
    // While the files are still being listed, those not listed yet are not dropped.
    const current = this.listing ? null : new Set(this.entries.map((e) => this.identityOf(e)));
    const files: Record<string, FileStatus> = Object.fromEntries(Object.entries(this.kept?.files ?? {}).filter(([id]) => !current || current.has(id)));
    const review = this.review;
    for (const e of this.entries) {
      const f = review.byPath.get(e.path);
      const d = this.diffs[e.path];
      if (!f || f.status === 'unknown' || f.waiting.length || !d?.set || d.shortfall) continue;
      files[this.identityOf(e)] = { total: f.total, done: f.done, reviewed: this.flagsOf(f, d.set.blocks), marks: this.fileMarks(e) };
    }
    // Per file, the pending keys it was looked through for and holds none of.
    const dead: Record<string, string> = {};
    const pending = this.legacy.filter((k) => !this.found.has(k));
    if (pending.length)
      for (const e of this.entries) {
        const looked = this.lookedFor.get(e.path);
        if (looked && pending.every((k) => looked.has(k))) dead[this.identityOf(e)] = marksSignature(pending);
      }
    const record: StatusRecord = { v: 2, at: Date.now(), files, ...(Object.keys(dead).length ? { dead } : {}) };
    this.kept = record;
    if (identityGeneration() !== this.generation) return;
    try {
      await this.statusStore.write(this.statusKey(), record);
    } catch {
      /* storage full or unavailable: the next load diffs the files to know */
    }
  }

  /** Save soon, once for every change of review state meanwhile. */
  private scheduleStatusSave() {
    if (this.disposed || this.statusTimer) return;
    this.statusTimer = setTimeout(() => void this.saveStatus(), STATUS_SAVE_MS);
  }

  private entryFor(f: PrFile): FileEntry {
    return {
      path: f.filename,
      previous: f.previous_filename ?? null,
      status: f.status,
      additions: f.additions,
      deletions: f.deletions,
      patch: f.patch ?? null,
      oldSha: null,
      newSha: f.status === 'removed' ? null : f.sha,
      category: categorize(f.filename, this.source.generated),
      noise: false,
      estimate: Math.max(f.additions + f.deletions, f.changes > 0 ? 1 : 0),
    };
  }

  /** Where a comparison from the track's base to the head starts (its merge base with the head): the first point of the track's history. */
  mergeBase(): Promise<string> {
    return this.getMergeBase();
  }

  /** Merge base of the PR's base branch and `to` (the head unless given), i.e. that snapshot's own base. */
  private getMergeBase(to?: string): Promise<string> {
    const base = this.source.track.base;
    if (!base.sha) return Promise.reject(new Error('the track has no base yet'));
    const tip = to ?? this.head ?? this.source.track.head;
    // The empty tree (a window reaching a root commit) has nothing in common with any commit but itself.
    if (base.sha === EMPTY_TREE || tip === EMPTY_TREE) return Promise.resolve(EMPTY_TREE);
    let p = this.mergeBases.get(tip);
    if (!p) {
      p = rest<{ merge_base_commit: { sha: string } }>(
        `repos/${this.owner}/${this.repo}/compare/${base.sha}...${tip}?per_page=1`,
      ).then((c) => c.merge_base_commit.sha);
      p.catch(() => this.mergeBases.delete(tip));
      this.mergeBases.set(tip, p);
    }
    return p;
  }

  /** The commit a `from`/`to` value names: a SHA, or `<sha>^` for that commit's first parent. */
  private async commitOf(value: string): Promise<string> {
    if (!value.endsWith('^')) return value;
    const sha = value.slice(0, -1);
    const c = await rest<{ parents: { sha: string }[] }>(`repos/${this.owner}/${this.repo}/git/commits/${sha}`);
    const parent = c.parents[0]?.sha;
    if (!parent) throw new Error(`commit ${sha.slice(0, 7)} has no parent to compare with`);
    return parent;
  }

  /** A file's text at a merge base, or null where it is absent (always, at the empty tree, without asking). */
  private contentAtBase(path: string, base: string, opts: { priority: Priority }): Promise<string | null> {
    return base === EMPTY_TREE ? Promise.resolve(null) : contentAt(this.owner, this.repo, path, base, opts);
  }

  /** Path → blob SHA for a commit, or null when GitHub truncated the recursive tree. */
  private tree(sha: string): Promise<Map<string, string> | null> {
    if (sha === EMPTY_TREE) return Promise.resolve(new Map());
    let p = this.trees.get(sha);
    if (!p) {
      p = rest<{ truncated?: boolean; tree: { path: string; type: string; sha: string }[] }>(
        `repos/${this.owner}/${this.repo}/git/trees/${sha}?recursive=1`,
      ).then((t) => (t.truncated ? null : new Map(t.tree.filter((e) => e.type === 'blob').map((e) => [e.path, e.sha]))));
      this.trees.set(sha, p);
    }
    return p;
  }

  /** Blob SHAs of `paths` at a commit (GraphQL `object(expression:)`, 100 per request). */
  private async blobShas(commit: string, paths: string[]): Promise<Map<string, string>> {
    const out = new Map<string, string>();
    if (commit === EMPTY_TREE) return out;
    for (let i = 0; i < paths.length; i += 100) {
      const chunk = paths.slice(i, i + 100);
      const vars: Record<string, unknown> = { owner: this.owner, repo: this.repo };
      chunk.forEach((p, j) => (vars['e' + j] = `${commit}:${p}`));
      const res = await graphql<{ repository: Record<string, { oid?: string } | null> | null }>(
        blobShasQuery(chunk.length),
        vars,
      );
      chunk.forEach((p, j) => {
        const oid = res.repository?.['f' + j]?.oid;
        if (oid) out.set(p, oid);
      });
    }
    return out;
  }

  private async initInterdiff(files: readonly PrFile[]) {
    const head = this.source.track.head;
    // Any two commits, in either order: current, replaced by a force-push
    // (fetched by SHA like any other), or `base`. `base` on one side means
    // the base the other side was built on, which a later rebase may have
    // moved (both `base`: the head's).
    const fromSha = this.mode.from && this.mode.from !== 'base' ? await this.commitOf(this.mode.from) : null;
    const toSha = this.mode.to === 'base' ? null : await this.commitOf(this.mode.to ?? head);
    const to = toSha ?? (await this.getMergeBase(fromSha ?? undefined));
    const from = fromSha ?? (await this.getMergeBase(toSha ?? undefined));
    let [ft, tt] = await Promise.all([this.tree(from), this.tree(to)]);
    const prFiles = new Map(files.map((f) => [f.filename, f]));
    if (!ft || !tt) {
      // A recursive tree was truncated (huge repository): compare the PR's own files only.
      const names = [...new Set(files.flatMap((f) => (f.previous_filename ? [f.filename, f.previous_filename] : [f.filename])))];
      [ft, tt] = await Promise.all([this.blobShas(from, names), this.blobShas(to, names)]);
      this.partial = true;
    }
    const paths = new Set([...ft.keys(), ...tt.keys()]);
    // A history filtered to a path shows only the files in it (subject.ts `pathOf`).
    const scope = pathOf(this.source.subject);
    const entries: FileEntry[] = [];
    for (const path of paths) {
      if (!inPath(scope, path)) continue;
      const a = ft.get(path) ?? null;
      const b = tt.get(path) ?? null;
      if (a === b) continue;
      const f = prFiles.get(path);
      entries.push({
        path,
        previous: null,
        status: !a ? 'added' : !b ? 'removed' : 'modified',
        additions: 0,
        deletions: 0,
        // Commentability follows GitHub's base…head patch when the interdiff ends at head.
        patch: to === head ? (f?.patch ?? null) : null,
        oldSha: a,
        newSha: b,
        category: categorize(path, this.source.generated),
        noise: !f,
        estimate: 1,
      });
    }
    entries.sort((x, y) => Number(x.noise) - Number(y.noise) || x.path.localeCompare(y.path));
    this.entries = entries;
    this.askListed();
    this.oldCommit = from;
    this.newCommit = to;
    const short = (s: string) => s.slice(0, 7);
    this.label =
      `${fromSha ? short(from) : 'base'} → ${!toSha ? 'base' : to === head ? 'head' : short(to)}` +
      (this.partial ? ' (pull request files only)' : '');
  }

  /** Commit files the PR does not change are shown at (the head this diff was built for). */
  get headCommit(): string | null {
    return this.repoTree?.commit ?? this.head ?? this.trackHead;
  }

  /** The old side's commit: for base…head the merge base, fetched on first use. */
  async resolveOldCommit(): Promise<string | null> {
    if (this.oldCommit) return this.oldCommit;
    if (this.interdiff || !this.source.track.base.sha) return null;
    try {
      this.oldCommit = await this.getMergeBase();
    } catch {
      return null;
    }
    return this.oldCommit;
  }

  /** True when the PR (this comparison) does not change `path`. */
  isUnchanged(path: string): boolean {
    return !this.entries.some((e) => e.path === path);
  }

  /** Show a file: a changed file's diff, or the full text of a file the PR does not change. */
  open(path: string) {
    if (!this.isUnchanged(path)) {
      this.ensure(path);
      return;
    }
    if (this.started.has(path)) return;
    this.started.add(path);
    this.unchanged.add(path);
    void this.loadUnchanged(path);
  }

  /** Load a file the PR does not change: its text at the head, as one unchanged block; `force` skips the size cap. */
  private async loadUnchanged(path: string, force = false, fresh = false) {
    const empty: FileDiff = {
      status: 'loading',
      set: null,
      oldLines: [],
      newLines: [],
      oldHl: null,
      newHl: null,
      oldScopes: null,
      newScopes: null,
      language: null,
      failure: null,
    };
    this.set(path, empty);
    const { owner, repo } = this;
    try {
      const commit = this.headCommit;
      if (!commit) throw new Error('the pull request is not loaded');
      // The tree gives the blob SHA (content-addressed cache) and size; without it, read the file at the commit.
      const tree = this.repoTree;
      if (tree && tree.status !== 'ready') await tree.load().catch(() => {});
      if (tree?.lazy) await tree.reveal(path);
      const known = tree?.files.get(path) ?? null;
      if (!force && known?.size && known.size > MAX_FILE_SIZE) {
        this.set(path, { ...empty, status: 'too-large' });
        return;
      }
      let text: string | null;
      if (known) {
        const b = await blob(owner, repo, known.sha, { priority: 'interactive' });
        if (b.binary) {
          this.set(path, { ...empty, status: 'binary' });
          return;
        }
        text = b.text ?? '';
      } else {
        text = await contentAt(owner, repo, path, commit, { priority: 'interactive' });
        if (text === null) throw new Error(`${path} does not exist at ${commit.slice(0, 7)}`);
        if (text.slice(0, 8000).includes('\0')) {
          this.set(path, { ...empty, status: 'binary' });
          return;
        }
      }
      if (!force && text.length > MAX_FILE_SIZE) {
        this.set(path, { ...empty, status: 'too-large' });
        return;
      }
      const language = await eng.detectLanguage(path).catch(() => null);
      // The engine shows an unchanged file as unchanged blocks: identical texts, recomputed.
      const { set, shortfall } = await fileBlocks({
        path,
        oldText: text,
        newText: text,
        language,
        patch: null,
        recompute: true,
        options: { ignoreWhitespace: false, context: 3, algorithm: DEFAULT_ALGORITHM },
      }, { fresh });
      const lines = splitLines(text);
      this.set(path, { ...empty, status: 'ready', set, shortfall, oldLines: lines, newLines: lines, language });
      if (language && text) {
        const hl = await highlightCached(language, text, true);
        if (hl) this.set(path, { ...this.diffs[path], oldHl: hl.lines, newHl: hl.lines, oldScopes: hl.scopes, newScopes: hl.scopes });
      }
    } catch (err) {
      this.set(path, { ...empty, status: 'error', failure: failureOf(err) });
    }
  }

  /** Show a file the PR does not change that was held back as too large. */
  showLarge(path: string) {
    if (!this.unchanged.has(path) || this.diffs[path]?.status !== 'too-large') return;
    void this.loadUnchanged(path, true);
  }

  /** Load the changed files again after they failed to (then build the model as `init` does). */
  async retryFiles() {
    if (this.status !== 'error') return;
    this.status = 'loading';
    this.failure = null;
    await this.init();
  }

  /** Load a file again after it failed to, or after its diff came short (`shortfall`, trying its full computation afresh): a changed file, or one the PR does not change. */
  retry(path: string) {
    const d = this.diffs[path];
    if (d?.status !== 'error' && !d?.shortfall) return;
    if (this.unchanged.has(path)) void this.loadUnchanged(path, false, true);
    else if (this.entries.some((e) => e.path === path)) void this.load(path, 'interactive', true);
  }

  /**
   * Load a file now: it never waits behind background prefetch, which has
   * its own lane (at most `WANTED_FILES` of these load at once). A file
   * already being prefetched has its requests raised to interactive.
   */
  ensure(path: string) {
    this.opened = [...this.opened.filter((p) => p !== path), path].slice(-OPENED_FILES);
    const held = this.heldHighlights.get(path);
    if (held) {
      this.heldHighlights.delete(path);
      void held();
    }
    for (const [language, text] of this.highlighting.get(path) ?? []) prioritizeHighlight(language, text);
    if (this.started.has(path)) {
      this.raise(path);
      return;
    }
    this.background = this.background.filter((p) => p !== path);
    this.wanted = [path, ...this.wanted.filter((p) => p !== path)];
    this.pump();
  }

  /**
   * Queue files for background loading, in `order`. They load at most
   * `PREFETCH_FILES` at a time and start at most one per `PREFETCH_GAP_MS`,
   * so prefetch spends a small, steady share of the user's request budget.
   */
  prefetch(order: string[]) {
    for (const p of order)
      if (!this.started.has(p) && !this.wanted.includes(p) && !this.background.includes(p)) this.background.push(p);
    this.pump();
  }

  private pump() {
    if (this.disposed) return;
    while (this.activeWanted < WANTED_FILES && this.wanted.length) {
      const path = this.wanted.shift()!;
      if (this.started.has(path)) continue;
      this.activeWanted++;
      this.start(path, 'interactive', () => this.activeWanted--);
    }
    void this.pumpBackground();
  }

  /**
   * Start queued background files, in order, while fewer than
   * `PREFETCH_FILES` are loading: one whose texts are in this browser at
   * once; after one that makes requests, the next waits `REQUEST_GAP_MS`
   * for each.
   */
  private async pumpBackground() {
    if (this.pumping) return;
    this.pumping = true;
    try {
      while (!this.disposed && this.activeBackground < PREFETCH_FILES && this.background.length) {
        const path = this.background[0];
        if (this.started.has(path)) {
          this.background.shift();
          continue;
        }
        const requests = await this.requestsFor(path);
        if (this.disposed || this.background[0] !== path || this.started.has(path)) continue;
        if (requests > 0) {
          const wait = this.nextBackgroundAt - Date.now();
          if (wait > 0) {
            this.paceTimer ??= setTimeout(() => {
              this.paceTimer = null;
              this.pump();
            }, wait);
            return;
          }
          this.nextBackgroundAt = Date.now() + requests * REQUEST_GAP_MS;
        }
        this.background.shift();
        this.activeBackground++;
        this.start(path, 'prefetch', () => this.activeBackground--, PREFETCH_TIMEOUT_MS);
      }
    } finally {
      this.pumping = false;
    }
  }

  /**
   * The requests loading a changed file makes: none for a blob this browser
   * has; for base…head, the head blob, plus (without a patch) the merge
   * base and the base text; for an interdiff, both blobs.
   */
  private requestsFor(path: string): Promise<number> {
    let p = this.requests.get(path);
    if (!p) {
      const e = this.entries.find((x) => x.path === path);
      const { owner, repo } = this;
      const blobs = (sha: string | null) => (sha ? blobCached(owner, repo, sha).then((v) => (v ? 0 : 1)) : Promise.resolve(0));
      const merge = () => (this.mergeBases.size ? 0 : 1);
      p = !e
        ? Promise.resolve(REQUESTS_PER_FILE)
        : this.interdiff
          ? Promise.all([blobs(e.oldSha), blobs(e.newSha)]).then(([a, b]) => a + b)
          : e.status === 'removed'
            ? Promise.resolve(e.patch ? 0 : 1 + merge())
            : e.status === 'added' || e.patch || e.additions + e.deletions === 0
              ? blobs(e.newSha)
              : blobs(e.newSha).then((n) => n + 1 + merge());
      p = p.catch(() => REQUESTS_PER_FILE);
      this.requests.set(path, p);
    }
    return p;
  }

  /** Move `paths` (changed files in view) to the front of the background queue, in order; the rest keep theirs. */
  favor(paths: string[]) {
    const front = paths.filter((p) => this.background.includes(p));
    if (!front.length) return;
    this.background = [...front, ...this.background.filter((p) => !front.includes(p))];
    this.pump();
  }

  /** Files whose texts are being read at prefetch priority. */
  private prefetching = new Set<string>();

  /** Load `path` at `priority`; `done` releases its slot once it settles, or after `timeoutMs` when given. */
  private start(path: string, priority: Priority, done: () => void, timeoutMs?: number) {
    this.started.add(path);
    if (priority === 'prefetch') this.prefetching.add(path);
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      if (timer) clearTimeout(timer);
      done();
      this.pump();
      this.flushHighlights();
    };
    const timer = timeoutMs === undefined ? null : setTimeout(release, timeoutMs);
    void this.load(path, priority).finally(() => {
      this.prefetching.delete(path);
      release();
    });
  }

  /**
   * The viewer opened a file being prefetched: ask for its texts again at
   * interactive priority. Identical requests share one send, raised to the
   * most urgent caller's priority, so this sends nothing new.
   */
  private raise(path: string) {
    const e = this.prefetching.has(path) ? this.entries.find((x) => x.path === path) : undefined;
    if (!e) return;
    this.prefetching.delete(path);
    this.texts(e, 'interactive').catch(() => {});
  }

  private waiters = new Map<string, ((d: FileDiff) => void)[]>();

  private set(path: string, d: FileDiff) {
    this.diffs = { ...this.diffs, [path]: d };
    if (d.status !== 'loading') {
      for (const w of this.waiters.get(path) ?? []) w(d);
      this.waiters.delete(path);
    }
  }

  /**
   * Resolves once a file has finished loading (ready, binary or error),
   * loading it now (`ensure`) unless `ensure` is false: then it only waits
   * for a load already asked for (a prefetch, a retry, `open`).
   */
  settled(path: string, { ensure = true }: { ensure?: boolean } = {}): Promise<FileDiff> {
    const d = this.diffs[path];
    if (d && d.status !== 'loading') return Promise.resolve(d);
    if (ensure) this.ensure(path);
    return new Promise((resolve) => {
      const list = this.waiters.get(path) ?? [];
      list.push(resolve);
      this.waiters.set(path, list);
    });
  }

  /**
   * A changed file's two texts, read at `priority` (a prefetch read is
   * raised when the file is opened meanwhile), with GitHub's blob SHA of
   * each when it was read from one (null when rebuilt or read by path).
   */
  private async texts(e: FileEntry, priority: Priority): Promise<Texts | 'binary'> {
    const { owner, repo } = this;
    const opts = { priority };
    if (this.interdiff) {
      const [a, b] = await Promise.all([
        e.oldSha ? blob(owner, repo, e.oldSha, opts) : { text: '', binary: false },
        e.newSha ? blob(owner, repo, e.newSha, opts) : { text: '', binary: false },
      ]);
      if (a.binary || b.binary) return 'binary';
      return { old: a.text ?? '', new: b.text ?? '', oldSha: e.oldSha, newSha: e.newSha };
    }
    if (e.status === 'removed') {
      if (e.patch) return { old: applyPatch('', e.patch, 'reverse'), new: '', oldSha: null, newSha: null };
      const base = await this.getMergeBase();
      return { old: (await this.contentAtBase(e.path, base, opts)) ?? '', new: '', oldSha: null, newSha: null };
    }
    const head = e.newSha ? await blob(owner, repo, e.newSha, opts) : { text: '', binary: false };
    if (head.binary) return 'binary';
    const newText = head.text ?? '';
    const newSha = e.newSha;
    if (e.status === 'added') return { old: '', new: newText, oldSha: null, newSha };
    if (e.patch) return { old: applyPatch(newText, e.patch, 'reverse'), new: newText, oldSha: null, newSha };
    if (e.additions + e.deletions === 0) return { old: newText, new: newText, oldSha: newSha, newSha };
    const base = await this.getMergeBase();
    const old = await this.contentAtBase(e.previous ?? e.path, base, opts);
    return { old: old ?? '', new: newText, oldSha: null, newSha };
  }

  /** `fresh`: try the full computation again even where it failed before (fileBlocks.ts), as Try again does. */
  private async load(path: string, priority: Priority, fresh = false) {
    const e = this.entries.find((x) => x.path === path);
    if (!e) return;
    const empty: FileDiff = {
      status: 'loading',
      set: null,
      oldLines: [],
      newLines: [],
      oldHl: null,
      newHl: null,
      oldScopes: null,
      newScopes: null,
      language: null,
      failure: null,
    };
    this.set(path, empty);
    try {
      const [texts, language] = await Promise.all([
        this.texts(e, priority),
        eng.detectLanguage(path).catch(() => null),
      ]);
      if (texts === 'binary') {
        this.set(path, { ...empty, status: 'binary', language });
        return;
      }
      // The blob IDs marks name are hashed while the engine diffs.
      const [{ set, shortfall }, sides] = await Promise.all([
        fileBlocks({
          path,
          oldText: texts.old,
          newText: texts.new,
          language,
          patch: e.patch,
          recompute: this.interdiff || !e.patch,
          options: { ignoreWhitespace: this.mode.ignoreWhitespace, context: 3, algorithm: this.mode.algorithm },
        }, { fresh }),
        this.sidesOf(e, texts),
      ]);
      const base: FileDiff = {
        ...empty,
        status: 'ready',
        set,
        shortfall,
        oldLines: splitLines(texts.old),
        newLines: splitLines(texts.new),
        language,
        sides,
      };
      this.set(path, base);
      // The marks on what is shown, from wherever they were made (by a
      // side's computed ID too, where it differs), then on the file's other
      // blobs on the track.
      const shown = [sides.old, sides.new].flatMap((s) => {
        if (!s) return [];
        const alias = this.aliases.get(s.blob);
        return alias ? [s, { path: s.path, blob: alias }] : [s];
      });
      this.pairsOf(e.path, shown);
      this.ask(shown);
      if (!this.listedAsked.has(e.path)) {
        this.listedAsked.add(e.path);
        this.wantTrack(e.path);
      }
      this.reviewChanged();
      void this.resolveFile(path);
      this.scheduleMoves();
      // Highlighting goes on after the load: a prefetch slot waits for the
      // diff, not for it; a prefetched file's waits until every queued file
      // is diffed, so it never holds a status up in the engine's worker.
      if (language) {
        const run = () => this.highlight(path, language, texts);
        if (this.opened.includes(path)) void run();
        else this.heldHighlights.set(path, run);
      }
    } catch (err) {
      this.set(path, { ...empty, status: 'error', failure: failureOf(err) });
    }
  }

  /** Run the held highlighting once no prefetched file is queued or loading. */
  private flushHighlights() {
    if (this.disposed || this.background.length || this.activeBackground || this.activeWanted) return;
    const runs = [...this.heldHighlights.values()];
    this.heldHighlights.clear();
    for (const run of runs) void run();
  }

  /**
   * A changed file's sides as review marks (and notes, relay tasks and the
   * track's blobs) name them: the paths, and each text's ID, which is
   * GitHub's blob SHA (git's oid) whenever the text was read from a blob,
   * and `blobId` of the text only for a side rebuilt from a patch or read
   * by path. Where the oid and the decoded text's `blobId` differ (a
   * byte-order mark or invalid UTF-8 the decoding dropped), the computed
   * ID is kept as the side's alias (`aliases`): marks made on it before
   * sides were named by their oid still apply. The SHA is kept as the
   * computed ID's alias in the line map store too, so a text named by it
   * is fetched by the SHA.
   */
  private async sidesOf(e: FileEntry, texts: Texts): Promise<FileSides> {
    const side = async (exists: boolean, path: string, text: string, sha: string | null) => {
      if (!exists) return null;
      const computed = await blobId(text);
      const blob = sha ?? computed;
      this.blobTexts.set(blob, text);
      if (computed !== blob) {
        this.aliases.set(blob, computed);
        this.blobTexts.set(computed, text);
        if (identityGeneration() === this.generation) void this.lineMapStore.writeAlias(computed, blob);
      }
      return { path, blob };
    };
    const [o, n] = await Promise.all([
      side(e.status !== 'added', e.previous ?? e.path, texts.old, texts.oldSha),
      side(e.status !== 'removed', e.path, texts.new, texts.newSha),
    ]);
    return { old: o, new: n };
  }

  /**
   * As files are listed, before any is diffed: ask the marks on each one's
   * blobs GitHub already named (base…head lists the head's; an interdiff
   * names both ends'), and its blobs on the track, so a kept status and
   * the explorer's counts read the marks made in other views too.
   */
  private askListed() {
    const pairs: { path: string; blob: string }[] = [];
    for (const e of this.entries) {
      const mine: { path: string; blob: string }[] = [];
      if (e.newSha) mine.push({ path: e.path, blob: e.newSha });
      if (e.oldSha) mine.push({ path: e.previous ?? e.path, blob: e.oldSha });
      this.pairsOf(e.path, mine);
      pairs.push(...mine);
      if (!this.listedAsked.has(e.path)) {
        this.listedAsked.add(e.path);
        this.wantTrack(e.path);
      }
    }
    this.ask(pairs);
  }

  /** Files whose blobs on the track were asked for as they were listed. */
  private listedAsked = new Set<string>();

  /** The other ID of a side's text whose blob is `blob` (its computed `blobId`, where GitHub's SHA names it and the two differ), else undefined. */
  aliasOf(blob: string): string | undefined {
    return this.aliases.get(blob);
  }

  /** Record that file `path`'s marks are read from `pairs` (`filePairs`). */
  private pairsOf(path: string, pairs: readonly { path: string; blob: string }[]) {
    const set = this.filePairs.get(path) ?? this.filePairs.set(path, new Set()).get(path)!;
    for (const p of pairs) set.add(`${p.path}\0${p.blob}`);
  }

  /** Ask the marks of `pairs` (adding them to `asked`), from the marks store. */
  private ask(pairs: readonly { path: string; blob: string }[]) {
    const fresh = pairs.filter((p) => !this.asked.has(`${p.path}\0${p.blob}`));
    if (!fresh.length) return;
    this.asked = new Set([...this.asked, ...fresh.map((p) => `${p.path}\0${p.blob}`)]);
    this.source.marks.show(fresh);
    this.reviewChanged();
  }

  /**
   * Ask for the blobs of file `path` at the track's points (where its
   * history starts, then every point), whose marks the line map carries to
   * the blobs shown; files queued while one asking is under way go
   * together in the next (`blobsOnTrackPerPath` serves them all at once).
   * A file stays on the way (`reviewKnown` false) until everything it
   * needs was read: a reading that failed in part, or threw, is tried
   * again after CARRY_RETRY_MS, doubling up to CARRY_RETRY_MAX_MS, as line
   * maps are, TRACK_TRIES times in all (then `historyFailed`).
   */
  private wantTrack(path: string) {
    this.trackQueue.push(path);
    if (!this.onTheWay.has(path)) this.onTheWay = new Set([...this.onTheWay, path]);
    if (this.trackAsking) return;
    this.trackAsking = (async () => {
      // Files diffed in the same turn go together.
      await Promise.resolve();
      if (!this.startAsked) {
        this.startAsked = true;
        this.trackStart = await Promise.resolve()
          .then(() => this.mergeBase())
          .catch(() => null);
      }
      while (this.trackQueue.length && !this.disposed) {
        const batch = [...new Set(this.trackQueue)];
        this.trackQueue = [];
        const points = this.trackPointsNow;
        const at = points.join(',');
        let found: Map<string, FileOnTrack>;
        try {
          found = await this.trackBlobs(this.owner, this.repo, points, batch, this.newCommit);
        } catch (err) {
          console.warn('a file’s blobs on the track could not be read', batch, err);
          this.retryTrack(batch);
          continue;
        }
        if (this.disposed) break;
        const names = new Map(this.trackNames);
        const pairs: { path: string; blob: string }[] = [];
        for (const path of batch) {
          const file = found.get(path);
          if (!file) continue;
          const mine: { path: string; blob: string }[] = [];
          for (const [name, blobs] of file.blobs) for (const b of blobs) mine.push({ path: name, blob: b });
          this.pairsOf(path, mine);
          pairs.push(...mine);
          const others = [...file.blobs.keys()].filter((n) => n !== path);
          if (others.length) names.set(path, others);
          else names.delete(path);
        }
        this.trackNames = names;
        this.ask(pairs);
        await this.source.marks.lookedUp();
        const whole = batch.filter((p) => found.get(p)?.complete);
        for (const p of whole) {
          this.trackReadAt.set(p, at);
          this.trackFailures.delete(p);
        }
        if (whole.length) {
          this.onTheWay = new Set([...this.onTheWay].filter((p) => !whole.includes(p)));
          if (whole.some((p) => this.historyFailed.has(p))) this.historyFailed = new Set([...this.historyFailed].filter((p) => !whole.includes(p)));
        }
        this.retryTrack(batch.filter((p) => !whole.includes(p)));
      }
      this.trackAsking = null;
    })();
  }

  /**
   * Ask again for the blobs on the track of `paths`, whose reading failed,
   * after a backoff; they stay on the way meanwhile. A file failed
   * TRACK_TRIES times is not asked again until `retryHistory`.
   */
  private retryTrack(paths: readonly string[]) {
    const given: string[] = [];
    for (const p of paths) {
      const failures = (this.trackFailures.get(p) ?? 0) + 1;
      this.trackFailures.set(p, failures);
      if (failures >= TRACK_TRIES) {
        given.push(p);
        continue;
      }
      const timer = setTimeout(
        () => {
          this.trackRetries.delete(timer);
          if (!this.disposed) this.wantTrack(p);
        },
        Math.min(CARRY_RETRY_MAX_MS, CARRY_RETRY_MS * 2 ** (failures - 1)),
      );
      this.trackRetries.add(timer);
    }
    if (given.some((p) => !this.historyFailed.has(p))) this.historyFailed = new Set([...this.historyFailed, ...given]);
  }

  /** Read again the blobs on the track of every file given up on (`historyFailed`), TRACK_TRIES times more. */
  retryHistory() {
    const paths = [...this.historyFailed];
    if (!paths.length || this.disposed) return;
    this.historyFailed = new Set();
    for (const p of paths) {
      this.trackFailures.delete(p);
      this.wantTrack(p);
    }
  }

  private async highlight(path: string, language: string, texts: { old: string; new: string }) {
    const urgent = this.opened.includes(path);
    const sides = [texts.old, texts.new].filter((t) => t).map((t): [string, string] => [language, t]);
    this.highlighting.set(path, sides);
    const [oldH, newH] = await Promise.all([
      texts.old ? highlightCached(language, texts.old, urgent) : Promise.resolve(null),
      texts.new ? highlightCached(language, texts.new, urgent) : Promise.resolve(null),
    ]).finally(() => this.highlighting.delete(path));
    if ((oldH || newH) && this.diffs[path]?.status === 'ready')
      this.set(path, {
        ...this.diffs[path],
        oldHl: oldH?.lines ?? null,
        newHl: newH?.lines ?? null,
        oldScopes: oldH?.scopes ?? null,
        newScopes: newH?.scopes ?? null,
      });
  }

  private scheduleMoves() {
    if (this.movesTimer) clearTimeout(this.movesTimer);
    this.movesTimer = setTimeout(() => void this.computeMoves(), 400);
  }

  private async computeMoves() {
    const inputs = [];
    for (const [path, d] of Object.entries(this.diffs)) {
      if (d.status !== 'ready' || !d.set || this.unchanged.has(path)) continue;
      inputs.push({ path, runs: d.set.moveRuns });
    }
    if (!inputs.length) return;
    try {
      this.moves = await eng.detectMoves(inputs);
    } catch {
      /* moves are an enhancement */
    }
  }

  /**
   * The marks follow the reviewer's (stored and pending): every view of the
   * review state reads `review`, which follows them.
   */
  setMarks(marks: readonly Anchor[]) {
    if (marks === this.marks) return;
    this.marks = marks;
    this.reviewChanged();
  }

  /**
   * Compute these line maps too, as for marks (see `carry`), and add them
   * to `lineMaps`: what private notes on another blob of a file wait for
   * (state/fileNotes.svelte.ts).
   */
  wantLineMaps(needs: readonly { from: string; to: string }[]) {
    let fresh = false;
    for (const n of needs) {
      const key = lineMapKey(n.from, n.to);
      if (this.wantedMaps.has(key)) continue;
      this.wantedMaps.set(key, { from: n.from, to: n.to });
      fresh = true;
    }
    if (fresh) this.scheduleCarry();
  }

  /** The review state changed (marks, a file's diff, a line map): keep it, and compute the line maps it waits for. */
  private reviewChanged() {
    this.scheduleStatusSave();
    this.scheduleCarry();
  }

  private scheduleCarry() {
    if (this.disposed || this.carryTimer) return;
    this.carryTimer = setTimeout(() => {
      this.carryTimer = null;
      this.carry();
    }, 0);
  }

  /**
   * The comparison as the review model reads it: every changed file, with
   * its changes once computed, else the status a previous load kept for
   * the same marks, else GitHub's count.
   */
  private reviewDiff(): ReviewDiff {
    const files = this.entries.map((e): ReviewFile => {
      const d = this.diffs[e.path];
      const names = this.trackNames.get(e.path);
      const renamedFrom = [...new Set([...(e.previous ? [e.previous] : []), ...(names ?? [])])];
      if (d?.set && d.sides) {
        const kept = this.reviewFiles.get(e.path);
        if (kept && kept.diff.set === d.set && kept.diff.sides === d.sides && kept.names === names) return kept.file;
        const side = (s: FileSides['old']) => {
          if (!s) return null;
          const alias = this.aliases.get(s.blob);
          return { ...s, renamedFrom, ...(alias ? { aliases: [alias] } : {}) };
        };
        const f: ReviewFile = { path: e.path, old: side(d.sides.old), new: side(d.sides.new), changes: changesOf(d.set.blocks) };
        this.reviewFiles.set(e.path, { diff: d, file: f, names });
        return f;
      }
      // Not diffed yet: its blobs are not known, so no mark applies until it is.
      const k = this.keptStatus(e);
      const memo = this.pendingFiles.get(e);
      if (memo && memo.kept === k) return memo.file;
      const known = k && { total: k.total, done: k.done, status: statusOf(k.total, k.done) };
      const f: ReviewFile = { path: e.path, old: null, new: null, changes: null, known, estimate: e.estimate };
      this.pendingFiles.set(e, { kept: k, file: f });
      return f;
    });
    return { files, lineMaps: this.lineMaps };
  }

  /**
   * The review state of every changed file and of the pull request under
   * the marks (`review/model.ts`): the Reviewed checkboxes, n / p, the
   * explorer's icons and counts, the header's meters and the kept statuses
   * all read it. A file is reviewed again only when its own inputs change.
   */
  review: ReviewModel = $derived.by(() => this.reviewCache.review(this.reviewDiff(), this.reviewMarks));

  /**
   * Compute the line maps the review model waits for (marks on an earlier
   * blob of a file): the earlier blob is fetched unless this browser has
   * it, the later one is the text shown. Each pair is computed once; one
   * that cannot be (a blob GitHub no longer has, the engine unavailable)
   * leaves those marks covering nothing.
   */
  private carry() {
    if (this.disposed) return;
    for (const need of [...carryNeeds(this.review), ...this.wantedMaps.values()]) {
      if (this.carrying >= CARRY_AT_ONCE) {
        this.reviewChanged();
        return;
      }
      const key = lineMapKey(need.from, need.to);
      const to = this.blobTexts.get(need.to);
      if (this.carried.has(key) || to === undefined) continue;
      this.carried.add(key);
      this.carrying++;
      void this.lineMapOf(need.from, need.to, to)
        .then((map) => {
          if (this.disposed) return;
          // Without the engine, nothing is known: the pair is asked again
          // by a later model.
          if (map === 'no-engine') return;
          this.carryFailures.delete(key);
          // A text that cannot be had (GitHub says the blob does not
          // exist, or it is binary) maps no line, and nothing waits on it.
          this.lineMaps = new Map(this.lineMaps).set(key, map === 'gone' ? [] : map);
          this.reviewChanged();
        })
        .catch((e) => {
          // Anything else (a rate limit, the network, a 5xx, the engine
          // timing out) says nothing about the text: the file's review
          // state stays unknown (`reviewKnown`), and the pair is asked
          // again, later each time.
          if (this.disposed) return;
          const failures = (this.carryFailures.get(key) ?? 0) + 1;
          this.carryFailures.set(key, failures);
          console.warn('line map failed; trying again', key, e);
          const timer = setTimeout(
            () => {
              this.carryRetries.delete(timer);
              this.carried.delete(key);
              this.reviewChanged();
            },
            Math.min(CARRY_RETRY_MAX_MS, CARRY_RETRY_MS * 2 ** (failures - 1)),
          );
          this.carryRetries.add(timer);
        })
        .finally(() => {
          this.carrying--;
        });
    }
  }

  /**
   * The line map of blob `from` to the text `toText` (blob `to`); `gone`
   * when the earlier text cannot be had (GitHub answers 404 for the blob,
   * or it is binary), `no-engine` when the engine is unavailable. Throws
   * for any failure that says nothing about the text.
   */
  private async lineMapOf(from: string, to: string, toText: string): Promise<number[] | 'gone' | 'no-engine'> {
    const kept = await this.lineMapStore.read(from, to);
    if (kept) return kept;
    let fromText = this.blobTexts.get(from);
    if (fromText === undefined) {
      const sha = (await this.lineMapStore.readAlias(from)) ?? from;
      let b;
      try {
        b = await blob(this.owner, this.repo, sha, { priority: 'background' });
      } catch (e) {
        if (e instanceof HttpError && e.status === 404) return 'gone';
        throw e;
      }
      if (b.binary) return 'gone';
      fromText = b.text ?? '';
    }
    if (engineDegraded()) return 'no-engine'; // the fallback refuses (NeedsEngine)
    const map = await eng.lineMap(fromText, toText);
    if (identityGeneration() === this.generation) void this.lineMapStore.write(from, to, map);
    return map;
  }

  /** The changed file `path`'s review state; undefined for a file the comparison does not change. */
  fileReview(path: string): FileReview | undefined {
    return this.review.byPath.get(path);
  }

  /**
   * Removed and added lines of a changed file (`total`) and how many the
   * review marks cover (`done`), once its diff is computed or a previous
   * load kept its status for the same marks (`exact` either way); until
   * then GitHub's count, none done. The explorer's icons and counts, the
   * file header and the PR meter all read this.
   */
  progress(path: string): { total: number; done: number; exact: boolean } {
    const f = this.review.byPath.get(path);
    if (f) return { total: f.total, done: f.done, exact: f.status !== 'unknown' };
    // A file the PR does not change: nothing to review.
    const d = this.diffs[path];
    return { total: 0, done: 0, exact: !!d?.set };
  }

  /**
   * Whether each change block of a changed file is reviewed (none of its
   * removed and added lines unreviewed: `isUnreviewed` of the block's
   * lines under `coverage`), in order; for a file not diffed yet, what the
   * kept status says, as `progress`; null while unknown. n / p in other
   * files go by this, and the open file's changes by the same coverage.
   */
  changesReviewed(path: string): boolean[] | null {
    const f = this.review.byPath.get(path);
    const d = this.diffs[path];
    if (f && d?.set && f.status !== 'unknown' && !this.unchanged.has(path)) return this.flagsOf(f, d.set.blocks);
    if (this.unchanged.has(path) && d?.set) return d.set.blocks.filter((b) => b.kind === 'change').map(() => false);
    const e = this.byPath.get(path);
    return (e && this.keptStatus(e)?.reviewed) ?? null;
  }

  /**
   * Per change block of the file at `path`, its complexity score with the
   * rows found moved discounted (logic/complexity); null until its diff is
   * computed, and `unscored` for a diff the engine did not score
   * (`scored`): shown, but nothing will count it.
   */
  changesThink(path: string): number[] | 'unscored' | null {
    const set = this.diffs[path]?.set;
    if (!set || this.unchanged.has(path)) return null;
    if (!scored(set)) return 'unscored';
    return blockScores(set.blocks, movedRows(this.moves, path));
  }

  /** Withdraws this view's figures from agents (`provideComplexity`). */
  private unprovide: (() => void) | null = null;

  /** The file at `path`'s complexity for an agent, as this view scores it; null unless computed for blob `sha` (the agent's version). */
  private agentComplexity(path: string, sha: string | null): AgentFileComplexity | null {
    const e = this.byPath.get(path);
    const set = this.diffs[path]?.set;
    if (!e || !set || !scored(set) || e.newSha !== sha) return null;
    const blocks = agentBlocks(set.blocks, movedRows(this.moves, path));
    return { basis: 'view', think: blocks.reduce((n, b) => n + b.score, 0), blocks };
  }

  /** Per change block of `blocks`, whether it is reviewed under the file's coverage (`blockReviewed`). */
  private flagsOf(f: FileReview, blocks: Block[]): boolean[] {
    let flags = this.blockFlags.get(f);
    if (!flags) {
      const coverage = this.coverageOf(f, blocks);
      flags = blocks.flatMap((b, i) => (b.kind === 'change' ? [blockReviewed(b, coverage[i])] : []));
      this.blockFlags.set(f, flags);
    }
    return flags;
  }

  /** The coverage of each of `blocks` under the file's marks, computed once per review of the file. */
  private coverageOf(f: FileReview, blocks: Block[]): Coverage[] {
    let c = this.coverages.get(f);
    if (!c) {
      c = blocks.map((b) => blockCoverage(b, f.covers));
      this.coverages.set(f, c);
    }
    return c;
  }

  /**
   * Which rows of each block of a changed file the marks cover, one per
   * block (for display: folded runs, "N of M lines", where n lands); null
   * until its diff is computed.
   */
  coverage(path: string): Coverage[] | null {
    const f = this.review.byPath.get(path);
    const d = this.diffs[path];
    if (!f || !d?.set || f.status === 'unknown') return null;
    return this.coverageOf(f, d.set.blocks);
  }

  /** The writes that mark (`value`) or unmark `lines` of changed file `path`; null until its diff is computed. */
  planLines(path: string, lines: Iterable<Line>, value: boolean): Plan | null {
    const diff = this.reviewDiff();
    const file = diff.files.find((f) => f.path === path);
    return file?.changes ? planLines(diff, file, this.askedMarks, lines, value) : null;
  }

  /** The writes that mark or unmark every changed line of `path` (`review/model` `planFile`); null until its diff is computed. */
  planFile(path: string, value: boolean): Plan | null {
    const diff = this.reviewDiff();
    const file = diff.files.find((f) => f.path === path);
    return file ? planFile(diff, file, this.askedMarks, value) : null;
  }

  /** Every changed file has finished loading (diffed, binary or failed). */
  get allSettled(): boolean {
    return this.status === 'ready' && !this.listing && this.entries.every((e) => {
      const d = this.diffs[e.path];
      return !!d && d.status !== 'loading';
    });
  }

  /**
   * The reviewer's key-based marks waiting to be resolved (marks API v2
   * `legacy`), as `setLegacy` was last given them. Each changed file looks
   * for them as it loads, in this comparison, whatever it is (base…head,
   * an interdiff, a snapshot): docs/design.md "Review marks", key-based
   * marks.
   */
  legacy: readonly string[] = $state.raw([]);
  /** Called with what keys resolved to (and, on a closed pull request, which nothing resolves): the caller records it on the server. */
  onResolved: ((r: MarksResolution) => Promise<void> | void) | null = null;
  /** What each pending key resolved to here so far (by anchor ID), shown as marks until the server has it. */
  private found: ReadonlyMap<string, ReadonlyMap<string, Anchor>> = $state.raw(new Map());
  /** Per changed file, the keys it has looked for in both whitespace modes. */
  private lookedFor = new Map<string, Set<string>>();
  private resolving = new Set<string>();
  /** Keys sent to `onResolved` and not refused. */
  private posted = new Set<string>();
  private postTimer: ReturnType<typeof setTimeout> | null = null;

  /** The reviewer's marks on the pairs this comparison asked for (`asked`): none on another version of a path. */
  private askedMarks: readonly Anchor[] = $derived.by(() => {
    const asked = this.asked;
    const mine = this.marks.filter((m) => asked.has(`${m.path}\0${m.blob}`));
    return mine.length === this.marks.length ? this.marks : mine;
  });

  /** What pending key-based marks resolved to here, shown as marks until the server has them. */
  private resolvedHere: readonly Anchor[] = $derived.by(() => {
    if (!this.found.size) return [];
    const pending = new Set(this.legacy);
    return [...this.found].filter(([k]) => pending.has(k)).flatMap(([, as]) => [...as.values()]);
  });

  /** The marks the review model reads: the reviewer's asked ones, and what pending key-based marks resolved to here. */
  private reviewMarks: readonly Anchor[] = $derived(this.resolvedHere.length ? [...this.askedMarks, ...this.resolvedHere] : this.askedMarks);

  /** The reviewer's marks as `setMarks` gave them (the store's, any pair), with what resolved here: what kept statuses are keyed by. */
  private storeMarks: readonly Anchor[] = $derived(this.resolvedHere.length ? [...this.marks, ...this.resolvedHere] : this.marks);

  /** Bumped when a file finishes looking for keys (`lookedFor` is not reactive). */
  private lookedVersion = $state(0);

  /**
   * Whether changed file `path`'s review state is the one its marks give:
   * it is diffed, with the engine, no mark of it waits for a line map, and
   * every pending key-based mark was looked for in it. Until then a file
   * that reads "not reviewed" may only be missing marks still on their
   * way, so nothing is decided from it (GitHub's Viewed sync).
   */
  reviewKnown(path: string): boolean {
    void this.lookedVersion;
    const f = this.review.byPath.get(path);
    const d = this.diffs[path];
    if (!f || f.status === 'unknown' || d?.status !== 'ready' || engineDegraded() || f.waiting.length || this.onTheWay.has(path)) return false;
    // The track grew since its blobs were read (replaced commits found later): read them again.
    const readAt = this.trackReadAt.get(path);
    if (readAt !== undefined && readAt !== this.trackPointsNow.join(',')) {
      queueMicrotask(() => {
        if (!this.disposed && !this.onTheWay.has(path)) this.wantTrack(path);
      });
      return false;
    }
    // Every pair its marks are read from was answered by the marks store.
    for (const k of this.filePairs.get(path) ?? []) {
      const cut = k.indexOf('\0');
      if (!this.source.marks.answered(k.slice(0, cut), k.slice(cut + 1))) return false;
    }
    const looked = this.lookedFor.get(path);
    return this.legacy.every((k) => this.found.has(k) || looked?.has(k));
  }

  /** Some key-based mark resolved here is shown and not yet recorded on the server. */
  get unrecorded(): boolean {
    const pending = new Set(this.legacy);
    return [...this.found.keys()].some((k) => pending.has(k));
  }

  /**
   * Record now what pending keys resolved to here, and wait for the
   * server's answer: before a change shown reviewed through such a key is
   * unmarked, so the unmark finds the anchors stored. False when it could
   * not be recorded (offline, a failure): the caller leaves the marks as
   * they are and says so.
   */
  async recordResolved(): Promise<boolean> {
    if (this.postTimer) clearTimeout(this.postTimer);
    this.postTimer = null;
    await this.posting;
    await this.post();
    const pending = new Set(this.legacy);
    return [...this.found.keys()].every((k) => !pending.has(k) || this.posted.has(k));
  }

  /** The key-based marks still pending: every diffed file looks for those it has not yet. */
  setLegacy(keys: readonly string[]) {
    if (keys.length === this.legacy.length && keys.every((k, i) => k === this.legacy[i])) return;
    this.legacy = keys;
    const pending = new Set(keys);
    if ([...this.found.keys()].some((k) => !pending.has(k))) this.found = new Map([...this.found].filter(([k]) => pending.has(k)));
    for (const k of [...this.posted]) if (!pending.has(k)) this.posted.delete(k);
    for (const path of Object.keys(this.diffs)) void this.resolveFile(path);
  }

  /**
   * Look for the pending key-based marks in one changed file, in this
   * comparison's layouts and then the other whitespace mode's (a key made
   * ignoring whitespace only matches there). What a key covers is shown at
   * once and sent to `onResolved` soon after; a file that cannot be
   * resolved (failed, binary, the engine unavailable, shown plainer: its
   * layouts are not all there, so a key not found may be in one missing)
   * holds up no other, and is not looked through.
   */
  private async resolveFile(path: string) {
    const d = this.diffs[path];
    const e = this.byPath.get(path);
    if (!e || d?.status !== 'ready' || !d.set || d.shortfall || !d.sides || this.resolving.has(path) || engineDegraded()) return;
    const looked = this.lookedFor.get(path) ?? new Set<string>();
    const keys = this.legacy.filter((k) => !looked.has(k) && !this.found.has(k));
    if (!keys.length) return;
    // A file looked through before for exactly these keys, finding none
    // (this session, or as a previous load kept), is not looked through again.
    const id = this.identityOf(e);
    const dead = deadKeys.get(`${this.statusKey()}|${id}`) ?? this.kept?.dead?.[id];
    if (dead !== undefined && dead === marksSignature(keys)) {
      for (const k of keys) looked.add(k);
      this.lookedFor.set(path, looked);
      this.lookedVersion++;
      return;
    }
    this.resolving.add(path);
    try {
      const sides = d.sides;
      const take = (rs: { key: string; anchors: Anchor[] }[]) => {
        const next = new Map(this.found);
        let changed = false;
        for (const r of rs) {
          if (!r.anchors.length) continue;
          const into = new Map(next.get(r.key) ?? []);
          for (const a of r.anchors) into.set(anchorId(a), a);
          next.set(r.key, into);
          changed = true;
        }
        if (changed) {
          this.found = next;
          this.reviewChanged();
        }
      };
      take(await eng.resolveLegacy(d.set.blocks, sides, keys));
      const rest = keys.filter((k) => !this.found.has(k));
      const oldText = sides.old ? this.blobTexts.get(sides.old.blob) : '';
      const newText = sides.new ? this.blobTexts.get(sides.new.blob) : '';
      if (rest.length && oldText !== undefined && newText !== undefined && !engineDegraded()) {
        const { set: other, shortfall } = await fileBlocks({
          path,
          oldText,
          newText,
          language: d.language,
          patch: e.patch,
          recompute: this.interdiff || !e.patch,
          options: { ignoreWhitespace: !this.mode.ignoreWhitespace, context: 3, algorithm: this.mode.algorithm },
        });
        if (engineDegraded() || shortfall) return;
        take(await eng.resolveLegacy(other.blocks, sides, rest));
      }
      for (const k of keys) looked.add(k);
      this.lookedFor.set(path, looked);
      this.lookedVersion++;
      const none = keys.filter((k) => !this.found.has(k));
      if (none.length === keys.length) deadKeys.set(`${this.statusKey()}|${id}`, marksSignature(none));
      this.schedulePost();
      this.scheduleStatusSave();
    } catch (err) {
      console.warn('resolving key-based marks failed', path, err);
    } finally {
      this.resolving.delete(path);
    }
  }

  private schedulePost() {
    if (this.disposed || this.postTimer) return;
    this.postTimer = setTimeout(() => {
      this.postTimer = null;
      void this.post();
    }, 300);
  }

  /**
   * Send what keys resolved to and nobody has sent. Keys nothing here
   * resolves are given up on only on a closed (or merged) pull request,
   * on base…head at its head, once every changed file was looked through
   * (the server checks it is closed): until then another comparison may
   * resolve them, so they stay pending, never lost.
   */
  private posting: Promise<void> = Promise.resolve();

  private post(): Promise<void> {
    this.posting = this.posting.then(() => this.postNow());
    return this.posting;
  }

  private async postNow() {
    if (!this.onResolved || this.disposed) return;
    const pending = new Set(this.legacy);
    const resolved = [...this.found]
      .filter(([k]) => pending.has(k) && !this.posted.has(k))
      .map(([block_key, as]) => ({ block_key, anchors: [...as.values()].map(toWire) }));
    const unresolvable = this.givenUp().filter((k) => !this.posted.has(k));
    if (!resolved.length && !unresolvable.length) return;
    const sent = [...resolved.map((r) => r.block_key), ...unresolvable];
    for (const k of sent) this.posted.add(k);
    try {
      await this.onResolved({ resolved, unresolvable });
    } catch (err) {
      for (const k of sent) this.posted.delete(k);
      console.warn('recording resolved key-based marks failed', err);
    }
  }

  /** The pending keys nothing resolves, when the pull request is closed and every file of base…head at its head was looked through. */
  private givenUp(): string[] {
    if (!this.source.closed || this.interdiff || this.head !== this.source.track.head || !this.allSettled) return [];
    const keys = this.legacy.filter((k) => !this.found.has(k));
    for (const e of this.entries) {
      const d = this.diffs[e.path];
      if (d?.status === 'error') return [];
      if (d?.status !== 'ready' || !d.sides) continue;
      const looked = this.lookedFor.get(e.path);
      if (!keys.every((k) => looked?.has(k))) return [];
    }
    return keys;
  }

  /**
   * Added and removed lines for a file: the computed diff's once loaded,
   * else GitHub's counts. Interdiff entries have no GitHub counts, so they
   * are null until their diff is computed.
   */
  counts(path: string): { added: number; removed: number } | null {
    const d = this.diffs[path];
    if (d?.set) return { added: d.set.added, removed: d.set.removed };
    if (this.interdiff) return null;
    const e = this.entries.find((x) => x.path === path);
    return e ? { added: e.additions, removed: e.deletions } : null;
  }

  /**
   * Totals for the comparison shown: the PR's for base…head; for an
   * interdiff, the sum over the files computed so far (`complete` false
   * until every file is).
   */
  totals(): { added: number; removed: number; complete: boolean } {
    if (!this.interdiff) return { ...this.headTotals, complete: true };
    let added = 0;
    let removed = 0;
    let complete = true;
    for (const e of this.entries) {
      const c = this.counts(e.path);
      if (!c) complete = false;
      else if (!e.noise) {
        added += c.added;
        removed += c.removed;
      }
    }
    return { added, removed, complete };
  }

  /** Case-insensitive search over file names and both sides' full source. */
  matches(path: string, q: string): boolean {
    if (!q) return true;
    if (path.toLowerCase().includes(q)) return true;
    const d = this.diffs[path];
    if (d?.status === 'ready') return d.newLines.some((l) => l.toLowerCase().includes(q)) || d.oldLines.some((l) => l.toLowerCase().includes(q));
    const e = this.entries.find((x) => x.path === path);
    return !!e?.patch?.toLowerCase().includes(q);
  }

  /**
   * The Files view shows this diff: new comments anchor to its head (see
   * `DiffSource.showDiff`) until `hide`.
   */
  show() {
    this.shown = true;
    this.source.showDiff(this, this.head);
  }

  hide() {
    this.shown = false;
    this.source.hideDiff(this);
  }

  dispose() {
    this.hide();
    this.disposed = true;
    this.unprovide?.();
    this.unprovide = null;
    this.wanted = [];
    this.background = [];
    if (this.paceTimer) clearTimeout(this.paceTimer);
    if (this.movesTimer) clearTimeout(this.movesTimer);
    if (this.statusTimer) clearTimeout(this.statusTimer);
    if (this.carryTimer) clearTimeout(this.carryTimer);
    for (const t of this.carryRetries) clearTimeout(t);
    for (const t of this.trackRetries) clearTimeout(t);
    if (this.postTimer) clearTimeout(this.postTimer);
  }
}
